"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Gauge, CircleCheck as CheckCircle2, ShieldCheck, Box } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.06
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 12
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
const specCheckpoints = [{
  id: 1,
  title: "Machine & Motion System",
  detail: "Check what the advertised price covers.",
  icon: Gauge
}, {
  id: 2,
  title: "Safety Arena & Blower",
  detail: "Confirm whether these components are included.",
  icon: Box
}, {
  id: 3,
  title: "Warranty & Support",
  detail: "Review the terms available for the specific product.",
  icon: ShieldCheck
}];
export default function BullPricing() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: false
  } : {
    initial: "hidden",
    whileInView: "show",
    viewport: {
      once: true,
      amount: 0.25
    },
    variants: containerVariants
  };
  return <section id="new-mechanical-bull-pricing" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div {...animationProps} className="flex flex-col gap-10">
          {/* Main Heading */}
          <motion.div variants={shouldReduceMotion ? undefined : itemVariants} className="flex flex-col gap-3">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground">
              New Mechanical Bull Pricing
            </h2>
            <div className="flex items-center gap-3 pt-1">
              <div className="h-px bg-border flex-1" />
            </div>
          </motion.div>

          {/* Technical Card Frame */}
          <motion.div variants={shouldReduceMotion ? undefined : itemVariants} className="border border-border bg-card text-card-foreground shadow-xs rounded-none">
            <div className="grid grid-cols-1 lg:grid-cols-12">
              {/* Primary Narrative Column */}
              <div className="lg:col-span-7 p-6 sm:p-10 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-border">
                <div className="space-y-6">
                  <p className="text-base sm:text-lg leading-relaxed text-foreground font-sans">
                    The price of a new mechanical bull depends on the machine and the equipment included with it. One package may include a controller, inflatable safety area and blower, while another may be configured differently.
                  </p>

                  <p className="text-sm sm:text-base leading-relaxed text-muted-foreground font-sans">
                    Therefore, compare more than the headline price. Look at the complete equipment package, product specifications, available warranty, accessories and delivery requirements.
                  </p>

                  <div className="bg-muted/60 border-l-2 border-primary p-4 my-4">
                    <p className="text-sm sm:text-base font-medium text-foreground leading-relaxed">
                      Mechanical Bull Shop shows visible product pricing wherever possible so buyers can compare actual inventory rather than relying only on generic industry estimates.
                    </p>
                  </div>
                </div>

                {/* Primary CTA Area */}
                <div className="pt-8 mt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <Button asChild size="lg" className="rounded-none bg-primary text-primary-foreground font-mono font-semibold tracking-wider uppercase text-xs sm:text-sm px-6 py-6 transition-[background-color,transform] duration-200 hover:bg-primary/90 active:scale-[0.99] w-full sm:w-auto shadow-xs">
                    <Link to="/MechanicalBullPriceGuidePage">
                      READ THE MECHANICAL BULL PRICE GUIDE
                      <ArrowUpRight className="ml-2 size-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Technical Spec Breakdown Sidebar Column */}
              <div className="lg:col-span-5 bg-muted/30 p-6 sm:p-8 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 border-b border-border pb-3 mb-6">
                    <CheckCircle2 className="size-4 text-primary" />
                    <h3 className="font-mono text-xs uppercase font-semibold tracking-wider text-foreground">
                      WHAT TO COMPARE
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {specCheckpoints.map((spec, i) => {
                    const IconComponent = spec.icon;
                    return <div key={spec.id} data-index={i} className="border border-border/80 bg-background/80 p-4 transition-[border-color] duration-150 hover:border-primary/50">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-muted border border-border shrink-0">
                              <IconComponent className="size-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-serif uppercase font-bold text-sm text-foreground tracking-wide">
                                {spec.title}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1 leading-normal font-sans">
                                {spec.detail}
                              </p>
                            </div>
                          </div>
                        </div>;
                  })}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}